import React, { useState, useEffect, useRef } from "react";
import InputField from "components/fields/InputField";
import Checkbox from "components/checkbox";
import { useAuthContext } from "../../hooks/AuthContext";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const { login, loading, error, user } = useAuthContext();
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      navigate("/admin");
    }
  }, [user, navigate]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    gsap.from(card, {
      opacity: 0,
      y: 50,
      duration: 0.8,
      ease: "power3.out",
    });

    const fields = gsap.utils.toArray(".form-field");
    gsap.from(fields, {
      opacity: 0,
      y: 30,
      duration: 0.6,
      stagger: 0.1,
      ease: "power2.out",
      delay: 0.3,
    });
  }, []);

  const handleSubmit = async () => {
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      // error handled by hook
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center p-4 font-poppins">
      <div
        ref={cardRef}
        className="shadow-black/5 w-full max-w-md rounded-2xl bg-white p-8 shadow-xl dark:border dark:border-stone-700/50 dark:bg-stone-800/60 dark:shadow-2xl dark:backdrop-blur-lg md:p-12"
      >
        <h4 className="mb-4 text-center font-serif text-3xl font-bold text-gray-800 dark:text-white md:text-4xl">
          Welcome Back
        </h4>
        <p className="mb-8 text-center text-base text-gray-500 dark:text-gray-400">
          Enter your credentials to access your account.
        </p>

        <div className="mb-6 flex items-center gap-3">
          <div className="h-px w-full bg-gray-200 dark:bg-stone-700" />
          <p className="text-base text-gray-500 dark:text-gray-400"> Login </p>
          <div className="h-px w-full bg-gray-200 dark:bg-stone-700" />
        </div>

        <form
          autoComplete="on"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* Email */}
          <div className="form-field">
            <InputField
              variant="auth"
              extra="mb-4"
              label="Email*"
              placeholder="your@email.com"
              id="email"
              type="text"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(e.target.value)
              }
              autoComplete="username"
            />
          </div>

          {/* Password */}
          <div className="form-field">
            <InputField
              variant="auth"
              extra="mb-4"
              label="Password*"
              placeholder="Min. 8 characters"
              id="password"
              type="password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
              autoComplete="current-password"
            />
          </div>

          {/* Checkbox */}
          <div className="form-field mb-4 flex items-center justify-between px-2">
            <div className="flex items-center">
              <Checkbox
                checked={keepLoggedIn}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setKeepLoggedIn(e.target.checked)
                }
              />
              <p className="ml-2 text-sm font-medium text-gray-600 dark:text-stone-300">
                Keep me logged In
              </p>
            </div>
            <a
              className="text-sm font-medium text-nature-green hover:text-nature-green-dark dark:text-nature-green-light dark:hover:text-nature-green"
              href="#"
            >
              Forgot Password?
            </a>
          </div>

          {error && (
            <div className="mb-4 text-center text-sm text-red-500">{error}</div>
          )}

          <div className="form-field">
            <button
              type="submit"
              className="linear border-transparent mt-2 w-full rounded-full border bg-nature-green-dark py-3 text-base font-bold text-white shadow-lg shadow-nature-green-dark/20 transition duration-200 hover:bg-nature-green-dark/90 active:bg-nature-green-dark/80 disabled:bg-gray-300 dark:bg-nature-green dark:text-white dark:shadow-nature-green/30 dark:hover:bg-nature-green/90 dark:active:bg-nature-green/80"
              disabled={loading}
            >
              {loading ? "Signing In..." : "Sign In"}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <span className="text-sm font-medium text-gray-500 dark:text-stone-400">
            Not registered yet?
          </span>
          <button
            className="ml-1 text-sm font-medium text-nature-green hover:text-nature-green-dark dark:text-nature-green-light dark:hover:text-nature-green"
            onClick={() => navigate("/auth/sign-up")}
          >
            Create an account
          </button>
        </div>
      </div>
    </div>
  );
}
